/*
 * VENTURE HEALTH MAP — Controller + Auditor maintained, Boardroom landing screen.
 * Health state drives agent behavior platform-wide: poor health ⇒ increased activity.
 */
import { useEffect, useState } from "react";
import { HeartPulse, RefreshCw, Loader2 } from "lucide-react";
import { osApi } from "@/lib/api-client";
import { toast } from "sonner";

const STATE_CLS = {
  strong: "text-emerald-300 border-emerald-500/40",
  steady: "text-sky-300 border-sky-500/40",
  declining: "text-amber-400 border-amber-500/40",
  critical: "text-rose-300 border-rose-500/50",
};

export const VentureHealthMap = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [cycling, setCycling] = useState(false);
  const load = () => osApi.healthMap().then(setData).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const runCycle = async () => {
    setCycling(true);
    try {
      const r = await osApi.runCycle();
      toast.success(`Agent cycle complete — ${r.surfaced} item(s) surfaced across ${r.ventures} ventures`);
      load();
      window.dispatchEvent(new CustomEvent("uc:consensus-refresh"));
    } catch { toast.error("Cycle failed"); } finally { setCycling(false); }
  };

  return (
    <div className="border border-white/10 bg-black/40 p-4" data-testid="venture-health-map">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
          <HeartPulse size={11} className="text-rose-300" /> Venture Health Map
          <span className="text-white/30">· controller + auditor · drives agent cadence</span>
        </div>
        <button onClick={runCycle} disabled={cycling}
          className="uc-chip text-sky-300 hover:bg-sky-500/10 flex items-center gap-1.5"
          data-testid="run-cycle-btn">
          {cycling ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Run Cycle Now
        </button>
      </div>
      {!data ? <Loader2 className="animate-spin text-white/40" size={16} /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-2">
          {data.health.map((h) => (
            <div key={h.venture_id} className={`border bg-black/50 p-2.5 ${STATE_CLS[h.state] || "border-white/10"}`}
              data-testid={`health-${h.venture_id}`}
              title={`Revenue ${h.dimensions?.revenue_trend} · engagement ${h.dimensions?.engagement_trend} · content ${h.dimensions?.content_recency_days}d · ${h.dimensions?.open_items} open items`}>
              <div className="text-xs text-white truncate">{h.venture}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="font-mono-uc text-[9px] uppercase">{h.state}</span>
                <span className="font-mono-uc text-sm">{h.score}</span>
              </div>
              <div className="h-1 bg-white/10 mt-1.5">
                <div className={`h-1 ${h.state === "strong" ? "bg-emerald-400" : h.state === "steady" ? "bg-sky-400" : h.state === "declining" ? "bg-amber-400" : "bg-rose-400"}`}
                  style={{ width: `${h.score}%` }} />
              </div>
            </div>
          ))}
          {data.health.length === 0 && (
            <div className="col-span-full text-white/40 text-xs p-3" data-testid="health-empty">
              No registered ventures yet — health computes automatically on registration.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
